// Tankování — „Noční kokpit": litry + cena vedle sebe, služby (Myčka, AdBlue,
// ostřikovače) se zaškrtnutím a cenou, „Jiné" s popisem. Payload beze změny
// (liters, total_czk, vat_rate, extras).

import { useState } from "react";
import { useHistory } from "react-router-dom";
import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import type { FuelingExtra } from "@routera/shared";
import { api } from "../lib/api";
import { useShiftStore } from "../store/shiftStore";
import { ThemeToggle } from "../components/ThemeToggle";

const VAT_RATE = 0.21;
const QUICK_EXTRAS = ["Myčka", "AdBlue", "Kapalina ostřikovačů"];

function uuid(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

export function FuelingScreen() {
  const history = useHistory();
  const shift = useShiftStore((s) => s.shift);

  const [liters, setLiters] = useState("");
  const [totalCzk, setTotalCzk] = useState("");
  // Zaškrtnuté služby s cenou; „Jiné" má navíc volný popis.
  const [srv, setSrv] = useState<Record<string, string>>({});     // label -> cena
  const [otherOn, setOtherOn] = useState(false);
  const [otherLabel, setOtherLabel] = useState("");
  const [otherAmount, setOtherAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!shift) {
    return (
      <IonPage>
        <IonContent scrollY={false}>
          <div className="ck scenebg" style={{ height: "100%" }}>
            <div className="pad-x"><h1>Tankování</h1><div className="h-sub">Není aktivní směna.</div></div>
            <div className="bottom"><button className="cta quiet" onClick={() => history.replace("/")}>Zpět</button></div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const litersNum = parseFloat(liters.replace(",", "."));
  const totalNum = parseFloat(totalCzk.replace(",", "."));
  const vatCzk = Number.isFinite(totalNum)
    ? Math.round(totalNum * VAT_RATE / (1 + VAT_RATE) * 100) / 100
    : null;
  const canSubmit = Number.isFinite(litersNum) && litersNum > 0 && Number.isFinite(totalNum) && totalNum > 0;

  const toggleSrv = (label: string) => {
    setSrv((prev) => {
      const next = { ...prev };
      if (label in next) delete next[label];
      else next[label] = "";
      return next;
    });
  };

  const buildExtras = (): FuelingExtra[] => {
    const out: FuelingExtra[] = [];
    for (const [label, val] of Object.entries(srv)) {
      const amount = parseFloat(val.replace(",", "."));
      if (Number.isFinite(amount) && amount > 0) out.push({ label, amount_czk: amount });
    }
    if (otherOn) {
      const amount = parseFloat(otherAmount.replace(",", "."));
      const label = otherLabel.trim();
      if (label && Number.isFinite(amount) && amount > 0) out.push({ label, amount_czk: amount });
    }
    return out;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.shift.fueling(shift.id, {
        id: uuid(),
        ts: new Date().toISOString(),
        liters: litersNum,
        total_czk: totalNum,
        vat_rate: VAT_RATE,
        extras: buildExtras(),
      });
      history.goBack();
    } catch (e: any) {
      setError(e?.message ?? "Uložení se nepovedlo");
      setSubmitting(false);
    }
  };

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="ck scenebg" style={{ height: "100%" }}>
          <div className="top">
            <button className="back" onClick={() => history.goBack()}>‹</button>
            <span className="wm">ROUTERA <em>DRIVER</em></span>
            <span className="live"><span className="dot" />živě</span>
            <ThemeToggle />
          </div>

          <div className="stopbody"><div className="pad-x">
            <h1>Tankování</h1>
            <div className="h-sub">Čas a poloha se uloží samy.</div>

            {error && (
              <div className="card" role="alert" style={{ marginTop: 12, borderColor: "rgba(242,101,88,.5)", background: "var(--bad-dim)" }}>
                <b style={{ color: "var(--bad)", fontSize: 14 }}>{error}</b>
              </div>
            )}

            <div className="tkrow" style={{ marginTop: 16 }}>
              <div className="tkfield">
                <input inputMode="decimal" placeholder="0" value={liters}
                  onChange={(e) => setLiters(e.target.value)} />
                <span className="unit">l</span>
              </div>
              <div className="tkfield">
                <input inputMode="decimal" placeholder="0" value={totalCzk}
                  onChange={(e) => setTotalCzk(e.target.value)} />
                <span className="unit">Kč</span>
              </div>
            </div>
            {vatCzk !== null && Number.isFinite(totalNum) && (
              <div style={{ fontSize: 12, color: "var(--dim)", margin: "0 2px 4px" }}>
                bez DPH <b className="mono" style={{ color: "var(--soft)" }}>{(totalNum - vatCzk).toFixed(2)} Kč</b>
              </div>
            )}

            <div className="cap" style={{ margin: "14px 0 8px" }}>Služby</div>
            {QUICK_EXTRAS.map((label) => {
              const on = label in srv;
              return (
                <div className={`srvrow${on ? " on" : ""}`} key={label}>
                  <button className="srvchk" onClick={() => toggleSrv(label)}>
                    <span className="bx">✓</span> {label}
                  </button>
                  <div className="tkfield prc">
                    <input inputMode="numeric" placeholder="0" value={srv[label] ?? ""}
                      onChange={(e) => setSrv((p) => ({ ...p, [label]: e.target.value }))} />
                    <span className="unit">Kč</span>
                  </div>
                </div>
              );
            })}
            <div className={`srvrow${otherOn ? " on" : ""}`}>
              <button className="srvchk" onClick={() => setOtherOn((v) => !v)}>
                <span className="bx">✓</span> Jiné
              </button>
              <div className="tkfield prc">
                <input inputMode="numeric" placeholder="0" value={otherAmount}
                  onChange={(e) => setOtherAmount(e.target.value)} />
                <span className="unit">Kč</span>
              </div>
            </div>
            {otherOn && (
              <div className="tkrow" style={{ marginBottom: 8 }}>
                <div className="tkfield">
                  <input placeholder="Napiš co to bylo" value={otherLabel}
                    onChange={(e) => setOtherLabel(e.target.value)} />
                </div>
              </div>
            )}
          </div></div>

          <div className="bottom">
            {submitting ? (
              <div style={{ textAlign: "center", padding: 10 }}><IonSpinner name="crescent" /></div>
            ) : (
              <button className={`cta${!canSubmit ? " locked" : ""}`} disabled={!canSubmit} onClick={() => void handleSubmit()}>
                Uložit tankování
              </button>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
